import { Info, Check } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Info icon + tooltip shown on the Available cell of a PRE-PRINTED SKU, telling
 * the operator in concrete numbers how its sellable Shopify quantity is built:
 * pre-printed units in stock plus blanks still available to sell.
 *
 * The "blanks" number is the blank's commit-path available (current minus
 * committed minus unavailable) — i.e. free to commit / sellable, NOT the
 * pick-floor printable count. The copy reflects that ("available to sell").
 *
 * 57.1-07: this is shown ONLY for pre-printed SKUs (`skus.is_preprinted = true`)
 * at every location where their derived blank has stock, mirroring the composite
 * the inventory-push worker actually pushes (Plan 06 — `fetchHybridLocationIds`).
 * It is NO LONGER gated on `locations.production_settings.hybrid_fulfillment_mode
 * === 'coordinator_confirm'` (that flag was HQ-only and is dead for the push).
 * A base garment / blank IS the blank, so it never shows this helper. The
 * `hybridMode` / `hybridRelatedQty` / etc. fields come from
 * `getProductLocationBreakdown`, already gated server-side on `is_preprinted`.
 *
 * Plan 07 narrowed the display to `is_preprinted` PAP only, so the breakdown is
 * always rendered from the PAP side (own pre-print stock + a derived blank). The
 * former "blank view" branch (a blank summarising its linked pre-prints) is no
 * longer reachable — the backend never emits `hybridMode` for a base garment —
 * so it has been removed along with its `linkedCount` plumbing.
 */

type Props = {
  /** This PAP row's own commit-path available (pre-printed units sellable here). */
  ownQty: number;
  /** The derived blank's commit-path available at this location. */
  relatedQty: number;
  /** The blank SKU string. `null` when not resolvable for display. */
  relatedSku: string | null;
  className?: string;
};

export function AdditiveBreakdownIcon({
  ownQty,
  relatedQty,
  relatedSku,
  className,
}: Props) {
  const total = ownQty + relatedQty;

  // Concise aria-label collapses the whole tooltip into a single sentence for SR users.
  const ariaLabel = `Pre-printed product. ${ownQty} pre-printed in stock plus ${relatedQty} blank${relatedQty === 1 ? "" : "s"} available to sell equals ${total} sellable on Shopify.`;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            className={`inline-flex items-center text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none rounded ${className ?? ""}`}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="p-0 max-w-xs">
          <HybridTooltipBody
            ownQty={ownQty}
            relatedQty={relatedQty}
            relatedSku={relatedSku}
            total={total}
          />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function HybridTooltipBody({
  ownQty,
  relatedQty,
  relatedSku,
  total,
}: {
  ownQty: number;
  relatedQty: number;
  relatedSku: string | null;
  total: number;
}) {
  return (
    <div className="w-72 text-sm">
      {/* Header: green check + state label. Colour AND text carry the state. */}
      <div className="flex items-center gap-2 px-3 pt-3 pb-2">
        <span
          aria-hidden="true"
          className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
        >
          <Check className="h-3 w-3" />
        </span>
        <span className="font-medium text-foreground">Pre-printed product</span>
      </div>

      {/* Body copy: one concrete sentence. No filler, no hedging. */}
      <div className="px-3 pb-2 text-muted-foreground">
        <p className="leading-relaxed">
          <span className="font-semibold tabular-nums text-foreground">{relatedQty}</span>{" "}
          blank{relatedQty === 1 ? "" : "s"}
          {relatedSku ? (
            <>
              {" "}
              (
              <span className="font-mono text-xs">{relatedSku}</span>
              )
            </>
          ) : null}{" "}
          {relatedQty === 1 ? "is" : "are"} available to sell here, so this
          product stays sellable after the pre-printed stock runs out.
        </p>
      </div>

      {/* Formula block — tight grid, numbers right-aligned, tabular-nums for consistency */}
      <div className="border-t px-3 py-2.5">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
          Sellable on Shopify
        </div>
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 text-sm tabular-nums">
          <dt className="text-muted-foreground">Pre-printed in stock</dt>
          <dd className="text-foreground">{ownQty}</dd>
          <dt className="text-muted-foreground">Blanks available to sell</dt>
          <dd className="text-foreground">+ {relatedQty}</dd>
          {/* Divider: a presentational rule spanning both columns. Rendered as a
              decorative div (not an empty <dt>) so screen readers don't announce
              an empty definition term. */}
          <div className="col-span-2 border-t border-border/60 mt-1 pt-1" aria-hidden="true" />
          <dt className="font-medium text-foreground">Sellable</dt>
          <dd className="font-semibold text-foreground">{total}</dd>
        </dl>
      </div>
    </div>
  );
}

/**
 * Inline copy strings for contexts where a tooltip isn't available. The
 * "available to sell" framing matches the tooltip above (the blank number is the
 * blank's commit-path available, i.e. free to commit / sellable, NOT the
 * pick-floor printable count) so both surfaces describe the identical value
 * identically and can't drift.
 *
 * `pushComposite` is consumed by `PushConfirmDialog` (the hybrid-qty notice).
 */
export const ADDITIVE_COPY = {
  featureName: "Pre-printed product",
  summary: "Sellable qty = pre-printed in stock + blanks available to sell.",
  papRow: "This pre-printed product's sellable qty includes the blanks available to sell.",
  noPaps: "This blank has no linked pre-prints; sellable qty equals on-hand.",
  pushComposite:
    "For pre-printed products, the quantity pushed to each location is the pre-printed units in stock plus the blanks available to sell there, so the product stays sellable after pre-printed stock runs out.",
};
